"use client";

import { useContentfulInspectorMode } from "@scott/contentful/live-preview-init";
import type { FieldInspectorProps, InspectorProps } from "../../lib/inspector";
import { Slider, type SliderProps } from "./slider";

export type SliderInspectorProps = InspectorProps<"container" | "slides">;

export type SliderSlideInspectorProps = InspectorProps<
  "container" | "eyebrow" | "title" | "subline" | "button0" | "button1"
>;

function mergeInspectorProps(
  supplied: FieldInspectorProps | undefined,
  generated: FieldInspectorProps | null | undefined
): FieldInspectorProps | undefined {
  if (!(supplied || generated)) {
    return;
  }

  return { ...supplied, ...generated };
}

export function SliderPreview(props: Readonly<SliderProps>) {
  const inspector = useContentfulInspectorMode();
  const slides = props.slides.map((slide) => {
    const supplied = slide.inspectorProps;

    return {
      ...slide,
      inspectorProps: {
        ...supplied,
        container: mergeInspectorProps(
          supplied?.container,
          inspector({
            entryId: slide.sys?.id,
            fieldId: "internalName",
          })
        ),
        eyebrow: mergeInspectorProps(
          supplied?.eyebrow,
          inspector({
            entryId: slide.title?.sys?.id,
            fieldId: "eyebrow",
            locale: slide.title?.sys?.locale,
          })
        ),
        title: mergeInspectorProps(
          supplied?.title,
          inspector({
            entryId: slide.title?.sys?.id,
            fieldId: "headline",
            locale: slide.title?.sys?.locale,
          })
        ),
        subline: mergeInspectorProps(
          supplied?.subline,
          inspector({
            entryId: slide.title?.sys?.id,
            fieldId: "subline",
            locale: slide.title?.sys?.locale,
          })
        ),
        button0: mergeInspectorProps(
          supplied?.button0,
          inspector({
            entryId: slide.buttons?.[0]?.sys?.id,
            fieldId: "text",
          })
        ),
        button1: mergeInspectorProps(
          supplied?.button1,
          inspector({
            entryId: slide.buttons?.[1]?.sys?.id,
            fieldId: "text",
          })
        ),
      },
    };
  });

  const containerInspector = inspector({
    entryId: props.sys?.id,
    fieldId: "internalName",
  });
  const slidesInspector = inspector({
    entryId: props.sys?.id,
    fieldId: "slides",
  });

  return (
    <Slider
      {...props}
      inspectorProps={{
        ...props.inspectorProps,
        container: mergeInspectorProps(
          props.inspectorProps?.container,
          containerInspector
        ),
        slides: mergeInspectorProps(
          props.inspectorProps?.slides,
          slidesInspector
        ),
      }}
      slides={slides}
    />
  );
}
